import type React from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { NAV_LINKS, COMPANY_INFO } from '../utils/constants';
import Icon from './ui/Icon';
import logoWhite from '../assets/images/logo(white).png';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  return (
    <footer className="bg-primary py-10 text-white">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid gap-8 md:grid-cols-4">
          {/* Column 1 - Logo and description */}
          <div className="md:col-span-1">
            <div className="flex items-center mb-4">
              {isHomePage ? (
                <ScrollLink
                  to="hero"
                  spy={true}
                  smooth={true}
                  offset={-120}
                  duration={500}
                  className="cursor-pointer"
                >
                  <img
                    src={logoWhite}
                    alt="IT Joy"
                    className="h-10 w-auto"
                  />
                </ScrollLink>
              ) : (
                <RouterLink to="/" className="cursor-pointer">
                  <img
                    src={logoWhite}
                    alt="IT Joy"
                    className="h-10 w-auto"
                  />
                </RouterLink>
              )}
              <h2 className="ml-2 text-xl font-bold">
                IT <span className="text-secondary">Joy</span>
              </h2>
            </div>
            <p className="mb-4 text-sm text-white/70">
              Надежный IT-партнер с 2005 года. Мы строим и обслуживаем IT-инфраструктуру, которая
              работает на ваш бизнес.
            </p>
            <div className="flex space-x-3">
              {COMPANY_INFO.socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-secondary"
                  aria-label={social.name}
                >
                  <Icon
                    name={social.name.toLowerCase() as 'linkedin' | 'facebook' | 'telegram'}
                    size={16}
                  />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2 - Navigation */}
          <div className="md:col-span-1">
            <h3 className="mb-4 text-lg font-semibold">Навигация</h3>
            <ul className="space-y-2">
              {NAV_LINKS.map((link) => (
                <li key={link.id}>
                  {isHomePage ? (
                    <ScrollLink
                      to={link.to}
                      spy={true}
                      smooth={true}
                      offset={-100}
                      duration={500}
                      className="text-white/70 transition-colors hover:text-secondary"
                    >
                      {link.label}
                    </ScrollLink>
                  ) : (
                    <RouterLink
                      to="/"
                      className="text-white/70 transition-colors hover:text-secondary"
                    >
                      {link.label}
                    </RouterLink>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Contact info */}
          <div className="md:col-span-1">
            <h3 className="mb-4 text-lg font-semibold">Контакты</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Icon name="location" className="mr-2 mt-1 text-secondary" size={16} />
                <span className="text-white/70">{COMPANY_INFO.address}</span>
              </li>
              <li className="flex items-start">
                <Icon name="phone" className="mr-2 mt-1 text-secondary" size={16} />
                <a
                  href={`tel:${COMPANY_INFO.phone}`}
                  className="text-white/70 transition-colors hover:text-secondary"
                >
                  {COMPANY_INFO.phone}
                </a>
              </li>
              <li className="flex items-start">
                <Icon name="email" className="mr-2 mt-1 text-secondary" size={16} />
                <a
                  href={`mailto:${COMPANY_INFO.email}`}
                  className="text-white/70 transition-colors hover:text-secondary"
                >
                  {COMPANY_INFO.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4 - Working hours */}
          <div className="md:col-span-1">
            <h3 className="mb-4 text-lg font-semibold">Режим работы</h3>
            <div className="flex items-start">
              <Icon name="clock" className="mr-2 mt-1 text-secondary" size={16} />
              <span className="text-white/70">{COMPANY_INFO.workingHours}</span>
            </div>
            <p className="mt-4 text-white/70">
              Техническая поддержка клиентов доступна в режиме 24/7 по договору обслуживания.
            </p>
          </div>
        </div>

        <hr className="my-8 border-white/10" />

        <div className="flex flex-col items-center justify-between space-y-4 text-sm text-white/50 md:flex-row md:space-y-0">
          <div>
            © {currentYear} IT Joy. Все права защищены.
          </div>
          <div className="flex space-x-6">
            <RouterLink to="/privacy-policy" className="hover:text-secondary">
              Политика конфиденциальности
            </RouterLink>
            <RouterLink to="/terms" className="hover:text-secondary">
              Условия использования
            </RouterLink>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
